import { useState, useContext, useEffect } from "react";

// @mui core components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Checkbox from "@mui/material/Checkbox";
import AddBoxIcon from "@mui/icons-material/AddBox";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

// Soft UI Dashboard PRO React components
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiBadge from "components/SuiBadge";

import AttachMoneyIcon from "@mui/icons-material/AttachMoney";

import toast from "react-hot-toast";
import { useForm, Controller } from "react-hook-form";
import { capitalCase } from "capital-case";

import { StoreContext } from "context/store";
import { MANUFACTURER_SERVICES } from "utils/data";

const TAG_MAX_LENGTH = 25;
const TAG_MAX_QUANTITY = 12;

const ServicesOffered = ({ onNext, onPrev, isNextDisabled, isPrevDisabled, saveNext }) => {
  const { store, updateStore } = useContext(StoreContext);
  const [checked, setChecked] = useState({});
  const [selectedTags, setSelectedTags] = useState({});
  const [newTag, setNewTag] = useState({});

  const {
    register,
    handleSubmit,
    trigger,
    setValue,
    watch,
    getValues,
    reset,
    formState: { errors },
    control,
  } = useForm();

  useEffect(() => {
    //set intial values
    const services_offered = store?.metadata?.services_offered || {};
    const steps = Object.keys(services_offered); //services stages
    let initialTags = {};
    let initialChecked = {};
    let stageServices = MANUFACTURER_SERVICES.map((item, index) => item.value);

    for (let i = 0; i < steps.length; i++) {
      if (stageServices.includes(steps[i])) {
        initialTags[steps[i]] = services_offered[steps[i]]["tags"];
        initialChecked[steps[i]] = true;

        setValue(`servicesDescription[${steps[i]}]`, services_offered[steps[i]]["descriptions"]);
      }
    }

    setSelectedTags(initialTags);
    setChecked(initialChecked);
  }, [store]);

  const handleSave = async (data) => {
    let services_offered = {};
    /**
     * checked,5 steps
        Ideation: true
        Production: true
        Sampling: true
        Sourcing: false
        Technical Design: true
     */
    const steps = Object.keys(checked); //services stages
    for (let i = 0; i < steps.length; i++) {
      if (checked[steps[i]] == true) {
        services_offered[steps[i]] = {
          tags: selectedTags[steps[i]],
          descriptions: data["servicesDescription"][steps[i]],
        };
      }
    }

    const payload = {
      metadata: {
        services_offered: services_offered,
      },
    };
    try {
      await updateStore(payload);
      toast.success("Update project requirements successfully!");
      saveNext();
    } catch (err) {
      return toast.error("Update project requirements failed!");
    }
  };

  const handleNewTagChange = (keyCode, step) => {
    const value = newTag[step] || "";
    if (!selectedTags[step]) {
      selectedTags[step] = [];
    }
    if (keyCode == "Comma" || keyCode == "Enter") {
      const newValue = value.trim().replace(",", "").toUpperCase();
      if (selectedTags[step] && selectedTags[step].length >= TAG_MAX_QUANTITY) {
        return toast.error(`You can have at most ${TAG_MAX_QUANTITY} tags!`);
      } else if (newValue.length >= TAG_MAX_LENGTH) {
        return toast.error(
          `Your customized tag can not have more than ${TAG_MAX_LENGTH} characters!`
        );
      } else if (newValue.length == 0) {
        return toast.error("Please input your customized tag!");
      } else if (selectedTags[step] && selectedTags[step].includes(newValue)) {
        return toast.error("You already have the tag!");
      }
      let newSelectedTags = { ...selectedTags };
      newSelectedTags[step] = [...newSelectedTags[step], newValue];

      setSelectedTags(newSelectedTags);
      let newValueInput = {};
      newValueInput[step] = "";
      setNewTag(newValueInput);
    }
  };

  const handleAddTag = (e, step) => {
    const newValue = newTag[step]?.trim().replace(",", "").toUpperCase();
    if (!selectedTags[step]) {
      selectedTags[step] = [];
    }
    if (selectedTags[step] && selectedTags[step].length >= TAG_MAX_QUANTITY) {
      return toast.error(`You can have at most ${TAG_MAX_QUANTITY} tags!`);
    } else if (!newValue) {
      return toast.error("Please input your customized tag!");
    } else if (newValue.length >= TAG_MAX_LENGTH) {
      return toast.error(
        `Your customized tag can not have more than ${TAG_MAX_LENGTH} characters!`
      );
    } else if (selectedTags[step] && selectedTags[step].includes(newValue)) {
      return toast.error("You already have the tag!");
    }
    let newSelectedTags = { ...selectedTags };
    newSelectedTags[step] = [...newSelectedTags[step], newValue];

    setSelectedTags(newSelectedTags);
    let newValueInput = {};
    newValueInput[step] = "";
    setNewTag(newValueInput);
  };

  const handleSelectTag = (value, step) => {
    if (!selectedTags[step]) {
      selectedTags[step] = [];
    }
    if (selectedTags[step] && selectedTags[step].includes(value)) {
      let newSelectedTags = { ...selectedTags };
      newSelectedTags[step] = selectedTags[step].filter((x) => x != value);
      setSelectedTags(newSelectedTags);
    } else {
      if (selectedTags[step] && selectedTags[step].length >= TAG_MAX_QUANTITY) {
        return toast.error(`You can have at most ${TAG_MAX_QUANTITY} tags!`);
      }
      let newSelectedTags = { ...selectedTags };
      newSelectedTags[step] = [...newSelectedTags[step], value];

      setSelectedTags(newSelectedTags);
    }
  };

  const handleRemoveTags = (value, step) => {
    if (!selectedTags[step]) {
      selectedTags[step] = [];
    }
    let newSelectedTags = { ...selectedTags };
    newSelectedTags[step] = selectedTags[step].filter((x) => x != value);
    setSelectedTags(newSelectedTags);
  };

  const getDescriptionHeight = (service) => {
    let h = 4;
    if (service == "Development") {
      h = 10;
    } else if (service == "Sourcing") {
      h = 6;
    } else if (service == "Production") {
      h = 16;
    }
    return h;
  };

  return (
    <Card id="services-offered" sx={{ overflow: "visible" }} className="my-2">
      <SuiBox p={3}>
        <SuiTypography variant="h5">Services Offered</SuiTypography>
      </SuiBox>
      <SuiBox component="form" pb={3} px={3} onSubmit={handleSubmit(handleSave)}>
        <List sx={{ width: "100%", bgcolor: "background.paper" }}>
          {MANUFACTURER_SERVICES.map((item, index) => {
            return (
              <ListItem key={index} disablePadding>
                <Card className="w-full border p-4 mb-2">
                  <div className="flex gap-1 flex-wrap items-center mb-2 mt-2">
                    <Checkbox
                      checked={checked[item.value] ? checked[item.value] : false}
                      defaultChecked={checked[item.value] ? checked[item.value] : false}
                      onClick={(e) => {
                        let newChecked = { ...checked };
                        newChecked[item.value] = e.target.checked;
                        setChecked(newChecked);
                      }}
                    />
                    <SuiBox lineHeight={0} display="inline-block">
                      <SuiTypography
                        component="label"
                        variant="h6"
                        fontWeight="bold"
                        textTransform="capitalize"
                        color={checked[item.value] ? "dark" : "secondary"}
                      >
                        {item.label}
                      </SuiTypography>
                    </SuiBox>
                    <div className="ml-6">
                      {selectedTags[item.value] &&
                        selectedTags[item.value].map((cat, index) => (
                          <SuiBadge
                            key={index}
                            color={item.color}
                            size="xs"
                            textTransform="none"
                            className="hover:cursor-pointer mr-1 mb-1"
                            badgeContent={
                              <div className="flex items-center">
                                {capitalCase(cat, { stripRegexp: /[^A-Z0-9\'\&\/\-_]/gi })}
                                <div
                                  className="hover:cursor-pointer"
                                  onClick={(e) => handleRemoveTags(cat, item.value)}
                                >
                                  <div className="text-gray-400 ml-2">X</div>
                                </div>
                              </div>
                            }
                            container
                          />
                        ))}
                    </div>
                  </div>
                  <div>
                    {selectedTags[item.value] &&
                    selectedTags[item.value].length == TAG_MAX_QUANTITY ? (
                      <SuiTypography
                        variant="text"
                        fontWeight="regular"
                        fontSize="12px"
                        color="error"
                        sx={{ ml: 2 }}
                      >
                        You can have at most {TAG_MAX_QUANTITY} tags!
                      </SuiTypography>
                    ) : (
                      ""
                    )}
                  </div>
                  <Grid
                    container
                    spacing={3}
                    className={`items-start ${!checked[item.value] ? "hidden" : "display"}`}
                  >
                    <Grid item xs={12} sm={12} md={12} lg={6}>
                      <SuiBox
                        display="flex"
                        flexDirection="column"
                        justifyContent="flex-end"
                        height="100%"
                      >
                        <div className="flex flex-col flex-wrap">
                          {item.children.map((cat, index) => (
                            <div className="ml-6">
                              <Checkbox
                                key={index}
                                variant="contained"
                                onClick={() => {
                                  if (checked[item.value]) {
                                    handleSelectTag(cat.value, item.value);
                                  }
                                }}
                                defaultChecked={
                                  selectedTags[item.value] &&
                                  selectedTags[item.value].includes(cat.value)
                                    ? true
                                    : false
                                }
                                checked={
                                  selectedTags[item.value] &&
                                  selectedTags[item.value].includes(cat.value)
                                    ? true
                                    : false
                                }
                                className="mr-1 my-1 hover:cursor-pointer"
                              />
                              <SuiTypography
                                component="label"
                                variant="h6"
                                fontWeight="regular"
                                textTransform="capitalize"
                                color={checked[item.value] ? "dark" : "secondary"}
                              >
                                {capitalCase(cat.label, { stripRegexp: /[^A-Z0-9\'\&\/\-_]/gi })}
                              </SuiTypography>
                            </div>
                          ))}
                        </div>
                        <div className="ml-6">
                          <SuiTypography
                            component="label"
                            variant="text"
                            fontWeight="regular"
                            textTransform="capitalize"
                            color={checked[item.value] ? "dark" : "secondary"}
                          >
                            Others
                          </SuiTypography>
                          <SuiInput
                            onChange={(e) => {
                              const step = item.value;
                              if (e?.target?.value) {
                                let newValueInput = {};
                                newValueInput[step] = e.target.value.toUpperCase();
                                setNewTag(newValueInput);
                              } else {
                                let newValueInput = {};
                                newValueInput[step] = "";
                                setNewTag(newValueInput);
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.code == "Enter" || e.code == "Comma") {
                                e.preventDefault();
                                handleNewTagChange(e.code, item.value);
                              }

                              return e;
                            }}
                            value={newTag[item.value]}
                            disabled={!checked[item.value]}
                            placeholder="Use ',' to confirm your customized services."
                            icon={{
                              direction: "right",
                              component: (
                                <AddBoxIcon
                                  className="hover:cursor-pointer"
                                  onClick={(e) => {
                                    if (checked[item.value]) {
                                      handleAddTag(e, item.value);
                                    }
                                  }}
                                />
                              ),
                            }}
                            componentsProps={{ input: { maxlength: TAG_MAX_LENGTH } }}
                          />
                          <SuiBox display="flex" justifyContent="space-between">
                            <div>
                              {newTag[item.value] && newTag[item.value].length > TAG_MAX_LENGTH ? (
                                <SuiTypography
                                  variant="text"
                                  fontWeight="regular"
                                  fontSize="12px"
                                  color="error"
                                  sx={{ ml: 2 }}
                                >
                                  Maxmium length should not exceed {TAG_MAX_LENGTH} characters
                                </SuiTypography>
                              ) : (
                                ""
                              )}
                            </div>
                            <SuiTypography
                              variant="text"
                              color="secondary"
                              fontWeight="regular"
                              fontSize="14px"
                            >
                              {newTag[item.value] ? newTag[item.value].length : 0}/{TAG_MAX_LENGTH}
                            </SuiTypography>
                          </SuiBox>
                        </div>
                      </SuiBox>
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} lg={6}>
                      <SuiBox
                        display="flex"
                        flexDirection="column"
                        justifyContent="flex-end"
                        height="100%"
                      >
                        <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                          <SuiTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold"
                            textTransform="capitalize"
                            color={checked[item.value] ? "dark" : "secondary"}
                          >
                            Descriptions
                          </SuiTypography>
                        </SuiBox>
                        <div className="flex content-end h-full">
                          <SuiInput
                            disabled={!checked[item.value]}
                            placeholder="Describe your services"
                            maxRows={12}
                            minRows={getDescriptionHeight(item.value)}
                            multiline={true}
                            fullWidth={true}
                            {...register(`servicesDescription[${item.value}]`)}
                          />
                        </div>
                      </SuiBox>
                    </Grid>
                  </Grid>
                </Card>
              </ListItem>
            );
          })}
        </List>

        <SuiBox
          pt={3}
          px={3}
          display="flex"
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          flexDirection={{ xs: "column", sm: "row" }}
        >
          {onPrev ? (
            <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
              <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                <SuiButton
                  variant="gradient"
                  color={isPrevDisabled ? "light" : "dark"}
                  sx={{ height: "100%" }}
                  onClick={onPrev}
                  disabled={isPrevDisabled}
                >
                  <ArrowBackIcon fontSize="large" />
                </SuiButton>
              </SuiBox>
            </SuiBox>
          ) : (
            <div />
          )}
          <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
            <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
              <SuiButton variant="gradient" color="dark" sx={{ height: "100%" }} type="submit">
                save
              </SuiButton>
            </SuiBox>
          </SuiBox>
          {onNext ? (
            <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
              <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                <SuiButton
                  variant="gradient"
                  color={isNextDisabled ? "light" : "dark"}
                  sx={{ height: "100%" }}
                  onClick={onNext}
                  disabled={isNextDisabled}
                >
                  <ArrowForwardIcon fontSize="large" />
                </SuiButton>
              </SuiBox>
            </SuiBox>
          ) : null}
        </SuiBox>
      </SuiBox>
    </Card>
  );
};

export default ServicesOffered;
